import React, { useEffect, useState } from 'react';
import './DepositPaymentsForm.css';

const MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;
const blankPayment = () => ({ received_date: '', amount: '', method: 'bank_transfer', reference: '', file: null });
const currency = amount => `RM ${Number(amount || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const amountNumber = value => Number(String(value ?? '').replace(/,/g, ''));
const goodMoney = value => /^\d+(?:\.\d{1,2})?$/.test(String(value).replace(/,/g, '')) && amountNumber(value) > 0;

export function buildDepositFormData(fields, deposit) {
  const data = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value != null && key !== 'venue_other') data.append(key, value);
  });
  data.set('required_deposit_amount', String(deposit.requiredAmount).replace(/,/g, ''));
  data.set('payments', JSON.stringify(deposit.payments.map((row, index) => ({
    ...(row.id ? { id: row.id } : {}),
    received_date: row.received_date,
    amount: String(row.amount).replace(/,/g, ''),
    method: row.method,
    reference: row.reference || '',
    ...(row.file ? { file_key: `receipt_${index}` } : {}),
  }))));
  deposit.payments.forEach((row, index) => {
    if (row.file) data.append(`receipt_${index}`, row.file, row.file.name);
  });
  return data;
}

function DepositPaymentsForm({ initialRequired = '', initialPayments = [], onChange }) {
  const [requiredAmount, setRequiredAmount] = useState(initialRequired == null ? '' : String(initialRequired));
  const [payments, setPayments] = useState(initialPayments.length ? initialPayments.map(row => ({
    id: row.id, received_date: row.received_date || '', amount: String(row.amount ?? ''),
    method: row.method || 'bank_transfer', reference: row.reference || '',
    receipt_filename: row.receipt_filename, file: null,
  })) : [blankPayment()]);

  useEffect(() => {
    const required = amountNumber(requiredAmount);
    const received = payments.reduce((sum, row) => sum + (amountNumber(row.amount) || 0), 0);
    const valid = goodMoney(requiredAmount) && payments.length > 0 && payments.length <= 10 &&
      payments.every(row => goodMoney(row.amount) && /^\d{4}-\d{2}-\d{2}$/.test(row.received_date) &&
        ['bank_transfer', 'card_terminal'].includes(row.method) &&
        (row.id || row.file) && (!row.file || (MIME_TYPES.includes(row.file.type) && row.file.size <= MAX_BYTES)));
    onChange?.({ requiredAmount, payments, receivedAmount: received,
      remainingAmount: Math.max(0, required - received), valid: !!valid });
  }, [requiredAmount, payments, onChange]);

  const update = (index, field, value) => setPayments(current => current.map((row, position) =>
    position === index ? { ...row, [field]: value } : row));
  const required = amountNumber(requiredAmount) || 0;
  const received = payments.reduce((sum, row) => sum + (amountNumber(row.amount) || 0), 0);
  const remaining = Math.max(0, required - received);

  return <section className="deposit-payments-form" aria-label="Deposit transactions">
    <h4>Deposit transactions</h4>
    <p>Enter every payment separately and attach its bank receipt or terminal slip. An admin will still check the bank or terminal statement.</p>
    <label>Deposit required (RM)
      <input type="text" inputMode="decimal" value={requiredAmount}
        onChange={event => setRequiredAmount(event.target.value)} required placeholder="5,000.00" />
    </label>
    {payments.map((row, index) => <div className="deposit-payment-row" key={index}>
      <div className="deposit-payment-row-title">
        <strong>Payment {index + 1}</strong>
        {payments.length > 1 && <button type="button" onClick={() => setPayments(current => current.filter((_, i) => i !== index))}>Remove</button>}
      </div>
      <div className="deposit-payment-grid">
        <label>Payment {index + 1} received date
          <input type="date" value={row.received_date} onChange={event => update(index, 'received_date', event.target.value)} required />
        </label>
        <label>Payment {index + 1} amount (RM)
          <input type="text" inputMode="decimal" value={row.amount} onChange={event => update(index, 'amount', event.target.value)} required />
        </label>
        <label>Payment {index + 1} method
          <select value={row.method} onChange={event => update(index, 'method', event.target.value)}>
            <option value="bank_transfer">Bank transfer</option><option value="card_terminal">Card terminal</option>
          </select>
        </label>
        <label>Payment {index + 1} reference (optional)
          <input value={row.reference} onChange={event => update(index, 'reference', event.target.value)} maxLength={100} />
        </label>
        <label>Payment {index + 1} receipt
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
            onChange={event => update(index, 'file', event.target.files?.[0] || null)} required={!row.id} />
        </label>
      </div>
      {row.receipt_filename && !row.file && <span className="deposit-payment-note">Existing receipt: {row.receipt_filename}</span>}
      {row.file && (!MIME_TYPES.includes(row.file.type) || row.file.size > MAX_BYTES) &&
        <span className="deposit-payment-error">Use a PDF, JPG, PNG, or WebP receipt under 5 MB.</span>}
    </div>)}
    <button type="button" className="deposit-add-payment" disabled={payments.length >= 10}
      onClick={() => setPayments(current => [...current, blankPayment()])}>Add payment</button>
    <div className="deposit-payment-totals">
      <span>Received: {currency(received)}</span><span>Remaining: {currency(remaining)}</span>
    </div>
    {required > 0 && received > required && <p className="deposit-payment-error">Overpaid by {currency(received - required)}. Check the amounts before saving.</p>}
    {required > 0 && remaining > 0 && <p className="deposit-payment-note">Approval waits until the full deposit is recorded.</p>}
  </section>;
}

export default DepositPaymentsForm;
